import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Tabs,
    Tab,
    Badge,
    Tooltip
} from '@mui/material';
import {
    Assignment,
    CheckCircle,
    Schedule,
    Warning,
    Error,
    PlayArrow,
    Refresh,
    CalendarToday,
    TrendingUp
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const RequiredTraining = ({ analytics }) => {
    const { user } = useAuth();
    const [trainingData, setTrainingData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTrainingData();
    }, []);

    const fetchTrainingData = async () => {
        try {
            setLoading(true);
            const [requiredContentRes, trackingRes] = await Promise.all([
                apiClient.get('/safety/content?isRequired=true'),
                apiClient.get('/safety/tracking')
            ]);

            const requiredContent = requiredContentRes.data.data || [];
            const tracking = trackingRes.data.data || [];

            // Merge required content with tracking data
            const merged = requiredContent.map(content => {
                const userTracking = tracking.find(t => t.safetyContent._id === content._id);
                return {
                    ...content,
                    userTracking
                };
            });

            setTrainingData(merged);
        } catch (error) {
            console.error('Error fetching training data:', error);
            setError('Failed to load training requirements');
        } finally {
            setLoading(false);
        }
    };

    const handleStartTraining = async (content) => {
        try {
            await apiClient.post(`/safety/tracking/${content._id}`, {
                status: 'in_progress'
            });
            fetchTrainingData(); // Refresh data
        } catch (error) {
            console.error('Error starting training:', error);
        }
    };

    const getStatusIcon = (tracking) => {
        if (!tracking) return <Schedule color="warning" />;
        
        switch (tracking.status) {
            case 'completed':
                return <CheckCircle color="success" />;
            case 'in_progress':
                return <Schedule color="warning" />;
            case 'overdue':
                return <Error color="error" />;
            default:
                return <Schedule color="warning" />;
        }
    };

    const getStatusColor = (tracking) => {
        if (!tracking) return 'warning';
        
        switch (tracking.status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'warning';
            case 'overdue':
                return 'error';
            default:
                return 'warning';
        }
    };

    const getStatusLabel = (tracking) => {
        if (!tracking) return 'Not Started';
        return tracking.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'critical':
                return 'error';
            case 'high':
                return 'warning';
            case 'medium':
                return 'info';
            default:
                return 'default';
        }
    };

    const getCategoryIcon = (category) => {
        // Reuse logic from other components
        return <Assignment color="primary" />;
    };

    const filterTrainingByStatus = (status) => {
        switch (status) {
            case 'pending':
                return trainingData.filter(t => !t.userTracking || t.userTracking.status === 'not_started');
            case 'in_progress':
                return trainingData.filter(t => t.userTracking?.status === 'in_progress');
            case 'completed':
                return trainingData.filter(t => t.userTracking?.status === 'completed');
            case 'overdue':
                return trainingData.filter(t => t.userTracking?.status === 'overdue');
            default:
                return trainingData;
        }
    };

    const calculateCompletionRate = () => {
        if (trainingData.length === 0) return 0;
        const completed = trainingData.filter(t => t.userTracking?.status === 'completed').length;
        return Math.round((completed / trainingData.length) * 100);
    };

    const getTabCounts = () => {
        return {
            pending: filterTrainingByStatus('pending').length,
            inProgress: filterTrainingByStatus('in_progress').length,
            completed: filterTrainingByStatus('completed').length,
            overdue: filterTrainingByStatus('overdue').length
        };
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    const tabCounts = getTabCounts();
    const completionRate = calculateCompletionRate();
    const filteredTraining = activeTab === 0 ? trainingData : 
                            activeTab === 1 ? filterTrainingByStatus('pending') :
                            activeTab === 2 ? filterTrainingByStatus('in_progress') :
                            activeTab === 3 ? filterTrainingByStatus('completed') :
                            filterTrainingByStatus('overdue');

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Assignment color="primary" fontSize="large" />
                <Typography variant="h5">
                    Required Training ({trainingData.length})
                </Typography>
            </Stack>

            {/* Overall Progress */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" gutterBottom>
                                Training Progress Overview
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={completionRate}
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Box>
                                <Typography variant="h6" color="primary">
                                    {completionRate}%
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {tabCounts.completed} of {trainingData.length} required training items completed
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <Stack spacing={1}>
                                {tabCounts.overdue > 0 && (
                                    <Alert severity="error" sx={{ py: 1 }}>
                                        <Typography variant="body2">
                                            {tabCounts.overdue} overdue items require immediate attention
                                        </Typography>
                                    </Alert>
                                )}
                                {tabCounts.inProgress > 0 && (
                                    <Alert severity="warning" sx={{ py: 1 }}>
                                        <Typography variant="body2">
                                            {tabCounts.inProgress} training items in progress
                                        </Typography>
                                    </Alert>
                                )}
                                {tabCounts.completed === trainingData.length && (
                                    <Alert severity="success" sx={{ py: 1 }}>
                                        <Typography variant="body2">
                                            All required training completed!
                                        </Typography>
                                    </Alert>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Status Tabs */}
            <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 3 }}
            >
                <Tab 
                    label="All Training" 
                    icon={<Assignment />}
                />
                <Tab 
                    label={
                        <Badge badgeContent={tabCounts.pending} color="warning" invisible={tabCounts.pending === 0}>
                            Pending
                        </Badge>
                    }
                    icon={<Schedule />}
                />
                <Tab 
                    label={
                        <Badge badgeContent={tabCounts.inProgress} color="info" invisible={tabCounts.inProgress === 0}>
                            In Progress
                        </Badge>
                    }
                    icon={<TrendingUp />}
                />
                <Tab 
                    label={
                        <Badge badgeContent={tabCounts.completed} color="success" invisible={tabCounts.completed === 0}>
                            Completed
                        </Badge>
                    }
                    icon={<CheckCircle />}
                />
                <Tab 
                    label={
                        <Badge badgeContent={tabCounts.overdue} color="error" invisible={tabCounts.overdue === 0}>
                            Overdue
                        </Badge>
                    }
                    icon={<Error />}
                />
            </Tabs>

            {/* Training List */}
            {filteredTraining.length === 0 ? (
                <Alert severity="info">
                    {activeTab === 0 ? 'No required training found.' :
                     activeTab === 1 ? 'No pending training items.' :
                     activeTab === 2 ? 'No training items in progress.' :
                     activeTab === 3 ? 'No completed training items.' :
                     'No overdue training items.'}
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {filteredTraining.map((training) => (
                        <Grid item xs={12} key={training._id}>
                            <Card sx={{
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': { 
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                                }
                            }}>
                                <CardContent>
                                    <Grid container spacing={3} alignItems="center">
                                        {/* Training Info */}
                                        <Grid item xs={12} md={6}>
                                            <Stack spacing={1}>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    {getCategoryIcon(training.category)}
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                        {training.title}
                                                    </Typography>
                                                    <Chip
                                                        label={training.priority}
                                                        size="small"
                                                        color={getPriorityColor(training.priority)}
                                                        variant="outlined"
                                                    />
                                                </Stack>
                                                
                                                <Typography variant="body2" color="text.secondary">
                                                    {training.description}
                                                </Typography>
                                                
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    <Chip
                                                        label={training.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={training.type}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                    />
                                                    {training.videoDuration && (
                                                        <Chip
                                                            label={`${Math.floor(training.videoDuration / 60)}min`}
                                                            size="small"
                                                            variant="outlined"
                                                            color="secondary"
                                                        />
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        {/* Status & Progress */}
                                        <Grid item xs={12} md={3}>
                                            <Stack spacing={2} alignItems="center">
                                                {getStatusIcon(training.userTracking)}
                                                <Chip
                                                    label={getStatusLabel(training.userTracking)}
                                                    color={getStatusColor(training.userTracking)}
                                                    variant="outlined"
                                                />
                                                
                                                {training.userTracking && training.userTracking.progressPercentage > 0 && (
                                                    <Box sx={{ width: '100%' }}>
                                                        <LinearProgress 
                                                            variant="determinate" 
                                                            value={training.userTracking.progressPercentage}
                                                            sx={{ borderRadius: 1 }}
                                                        />
                                                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                                            {training.userTracking.progressPercentage}% Complete
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {training.userTracking?.lastViewedAt && (
                                                    <Tooltip title={`Last viewed: ${new Date(training.userTracking.lastViewedAt).toLocaleDateString()}`}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <CalendarToday fontSize="small" color="action" />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(training.userTracking.lastViewedAt).toLocaleDateString()}
                                                            </Typography>
                                                        </Stack>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </Grid>

                                        {/* Actions */}
                                        <Grid item xs={12} md={3}>
                                            <Stack spacing={1}>
                                                {(!training.userTracking || training.userTracking.status === 'not_started') && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<PlayArrow />}
                                                        onClick={() => handleStartTraining(training)}
                                                        fullWidth
                                                    >
                                                        Start Training
                                                    </Button>
                                                )}
                                                
                                                {training.userTracking?.status === 'in_progress' && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<PlayArrow />}
                                                        onClick={() => handleStartTraining(training)}
                                                        fullWidth
                                                    >
                                                        Continue
                                                    </Button>
                                                )}
                                                
                                                {training.userTracking?.status === 'completed' && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Refresh />}
                                                        onClick={() => handleStartTraining(training)}
                                                        fullWidth
                                                    >
                                                        Review
                                                    </Button>
                                                )}

                                                {training.userTracking?.status === 'overdue' && (
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        startIcon={<Warning />}
                                                        onClick={() => handleStartTraining(training)}
                                                        fullWidth
                                                    >
                                                        Complete Now
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default RequiredTraining; 